import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import CodeMirror from '@uiw/react-codemirror';
import { useApp } from '../contexts/AppContext';
import { Card, Deck, PlayMode, Session, AIEvaluationResult, AISettings, PlayerSettings, UserCardPreference } from '../types';
import { SpacedRepetitionService } from '../services/SpacedRepetitionService';
import { getLanguageExtension, getThemeExtension, getLanguageDisplayName } from '../utils/codeEditorUtils';
import { EditorView } from '@codemirror/view';
import CodePopupModal from '../components/CodePopupModal';
import { getDatabaseService } from '../services/DatabaseFactory';
import { loadAISettings as loadAISettingsFromFactory, evaluateAnswer as evaluateAnswerViaFactory } from '../services/AIEvaluationFactory';

interface StudyCard extends Card {
  attempts: number;
  correct: number;
  lastAttempt?: Date;
  showAnswer: boolean;
  userAnswers: Record<string, string>;
}

const DeckPlayer: React.FC = () => {
  const { deckId } = useParams<{ deckId: string }>();
  const { state, loadDeck, loadCards } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Session state
  const [session, setSession] = useState<Session | null>(null);
  const [studyCards, setStudyCards] = useState<StudyCard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playMode, setPlayMode] = useState<PlayMode>('adaptive');
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [startTime, setStartTime] = useState<Date | null>(null);
  
  // Card interaction state
  const [selectedLanguage, setSelectedLanguage] = useState('javascript');
  const [userSelectedLanguage, setUserSelectedLanguage] = useState('javascript');
  const [userAnswer, setUserAnswer] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [responseStartTime, setResponseStartTime] = useState<Date | null>(null);

  // Track userAnswer changes with debugging
  useEffect(() => {
    preservedUserAnswerRef.current = userAnswer;
  }, [userAnswer]);
  
  // Sync userAnswer state when card or language changes
  useEffect(() => {
    if (studyCards.length > 0 && currentIndex >= 0 && currentIndex < studyCards.length) {
      const currentCard = studyCards[currentIndex];
      setUserAnswer(currentCard.userAnswers[userSelectedLanguage] || '');
    }
  }, [currentIndex, userSelectedLanguage, studyCards]);

  // Ensure valid language selection for current card
  useEffect(() => {
    if (studyCards.length > 0 && currentIndex >= 0 && currentIndex < studyCards.length) {
      const currentCard = studyCards[currentIndex];
      const availableLanguages = Object.keys(currentCard.languages);
      
      if (availableLanguages.length > 0) {
        // If current selected language is not available on this card, select the first available one
        if (!availableLanguages.includes(selectedLanguage)) {
          setSelectedLanguage(availableLanguages[0]);
        }
        if (!availableLanguages.includes(userSelectedLanguage)) {
          setUserSelectedLanguage(availableLanguages[0]);
        }
      }
    }
  }, [currentIndex, studyCards, selectedLanguage, userSelectedLanguage]);
  
  // AI evaluation state
  const [aiSettings, setAISettings] = useState<AISettings>({ provider: 'disabled' });
  const [aiEvaluation, setAIEvaluation] = useState<AIEvaluationResult | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluationError, setEvaluationError] = useState<string | null>(null);

  // Player settings
  const [playerSettings, setPlayerSettings] = useState<PlayerSettings>({ skipCompletedCards: true });

  // Code popup state
  const [showCodePopup, setShowCodePopup] = useState(false);
  const [popupCode, setPopupCode] = useState('');
  const [popupLanguage, setPopupLanguage] = useState('');
  const [popupTitle, setPopupTitle] = useState('');

  
  // Navigation and progress
  const [showCardList, setShowCardList] = useState(true);
  const [completedCards, setCompletedCards] = useState<Set<string>>(new Set());
  const [excludedCardIds, setExcludedCardIds] = useState<Set<string>>(new Set());
  const [sidebarWidth, setSidebarWidth] = useState(320); // 320px = w-80
  const [isResizing, setIsResizing] = useState(false);
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);
  
  // Random mode navigation history
  const [randomHistory, setRandomHistory] = useState<number[]>([]);
  const [randomHistoryIndex, setRandomHistoryIndex] = useState(-1);
  
  const spacedRepetitionService = useRef(new SpacedRepetitionService());
  
  // Navigation scrolling refs
  const navigationContainerRef = useRef<HTMLDivElement>(null);
  const cardItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const preservedUserAnswerRef = useRef<string>('');

  // Scroll navigation to current card
  const scrollToCurrentCard = () => {
    if (!navigationContainerRef.current || !cardItemRefs.current[currentIndex]) {
      return;
    }

    const container = navigationContainerRef.current;
    const cardElement = cardItemRefs.current[currentIndex];

    if (cardElement) {
      // Get container and element positions
      const containerRect = container.getBoundingClientRect();
      const elementRect = cardElement.getBoundingClientRect();
      
      // Calculate if element is visible
      const isVisible = 
        elementRect.top >= containerRect.top && 
        elementRect.bottom <= containerRect.bottom;

      if (!isVisible) {
        // Scroll to center the element in the container
        const containerHeight = container.clientHeight;
        const elementHeight = cardElement.offsetHeight;
        const scrollTop = cardElement.offsetTop - (containerHeight - elementHeight) / 2;
        
        container.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth'
        });
      }
    }
  };

  // Clean initialization without content preservation

  useEffect(() => {
    initializeSession();
  }, [deckId, location.search]);

  // Update card refs array when studyCards change
  useEffect(() => {
    cardItemRefs.current = cardItemRefs.current.slice(0, studyCards.length);
  }, [studyCards.length]);

  // Scroll to current card when currentIndex changes
  useEffect(() => {
    // Small delay to ensure DOM has updated
    const timer = setTimeout(scrollToCurrentCard, 100);
    return () => clearTimeout(timer);
  }, [currentIndex]);

  // Load settings once on component mount, not on every deck change
  useEffect(() => {
    loadAISettings();
    loadPlayerSettings();
  }, []);

  // Cleanup session on component unmount
  useEffect(() => {
    return () => {
      if (isSessionActive && session && startTime) {
        // End session silently on unmount
        const endTime = new Date();
        const duration = Math.round((endTime.getTime() - startTime.getTime()) / 1000);

        getDatabaseService().then(db => {
          return db.updateSession(session.id, {
            endTime: endTime.toISOString(),
            duration
          });
        }).catch(error => {
          console.error('Failed to end session on unmount:', error);
        });
      }
    };
  }, [isSessionActive, session, startTime]);

  const loadAISettings = async () => {
    try {
      const settings = await loadAISettingsFromFactory();
      setAISettings(settings);
    } catch (error) {
      console.error('Failed to load AI settings:', error);
    }
  };

  const loadPlayerSettings = async () => {
    try {
      if (window.electronAPI) {
        const settings = await window.electronAPI.getPlayerSettings();
        setPlayerSettings(settings);
      } else {
        // PWA mode: use localStorage
        const stored = localStorage.getItem('playerSettings');
        if (stored) {
          setPlayerSettings(JSON.parse(stored));
        }
      }
    } catch (error) {
      console.error('Failed to load player settings:', error);
    }
  };

  const handleSkipCompletedCardsToggle = async () => {
    const newValue = !playerSettings.skipCompletedCards;
    const newSettings = { ...playerSettings, skipCompletedCards: newValue };
    setPlayerSettings(newSettings);
    try {
      if (window.electronAPI) {
        await window.electronAPI.updatePlayerSettings({ skipCompletedCards: newValue });
      } else {
        // PWA mode: use localStorage
        localStorage.setItem('playerSettings', JSON.stringify(newSettings));
      }
    } catch (error) {
      console.error('Failed to save player settings:', error);
      // Revert on error
      setPlayerSettings({ ...playerSettings, skipCompletedCards: !newValue });
    }
  };

  const loadCardPreferences = async (deckId: string) => {
    try {
      const db = await getDatabaseService();
      const preferences = await db.getUserCardPreferences(deckId);
      const excludedIds = new Set(
        preferences.filter(pref => pref.isExcluded).map(pref => pref.cardId)
      );
      setExcludedCardIds(excludedIds);
    } catch (error) {
      console.error('Failed to load card preferences:', error);
    }
  };

  const loadCompletedCards = async (deckId: string) => {
    try {
      const db = await getDatabaseService();
      const completedIds = await db.getCompletedCardIds(deckId);
      console.log('📊 Loaded completed card IDs:', completedIds);
      setCompletedCards(new Set(completedIds));
    } catch (error) {
      console.error('Failed to load completed cards:', error);
    }
  };

  const isCardExcluded = (cardId: string): boolean => {
    return excludedCardIds.has(cardId);
  };

  const handleCardPreferenceToggle = async (cardId: string, isExcluded: boolean) => {
    if (!state.currentDeck) return;

    try {
      const db = await getDatabaseService();
      await db.updateCardPreference(cardId, state.currentDeck.id, isExcluded);

      // Update local state
      const newExcludedIds = new Set(excludedCardIds);
      if (isExcluded) {
        newExcludedIds.add(cardId);
      } else {
        newExcludedIds.delete(cardId);
      }
      setExcludedCardIds(newExcludedIds);
    } catch (error) {
      console.error('Failed to update card preference:', error);
    }
  };

  const getNextActiveCardIndex = (fromIndex: number): number | null => {
    for (let i = fromIndex + 1; i < studyCards.length; i++) {
      const cardId = studyCards[i].id;
      const isCompleted = playerSettings.skipCompletedCards && completedCards.has(cardId);
      if (!isCardExcluded(cardId) && !isCompleted) {
        return i;
      }
    }
    return null;
  };

  const getPreviousActiveCardIndex = (fromIndex: number): number | null => {
    for (let i = fromIndex - 1; i >= 0; i--) {
      const cardId = studyCards[i].id;
      const isCompleted = playerSettings.skipCompletedCards && completedCards.has(cardId);
      if (!isCardExcluded(cardId) && !isCompleted) {
        return i;
      }
    }
    return null;
  };

  const getActiveCardIndices = (): number[] => {
    return studyCards
      .map((_, index) => index)
      .filter(index => {
        const cardId = studyCards[index].id;
        const isCompleted = playerSettings.skipCompletedCards && completedCards.has(cardId);
        return !isCardExcluded(cardId) && !isCompleted;
      });
  };

  // Handle narrow screen detection
  useEffect(() => {
    const checkScreenWidth = () => {
      const isNarrow = window.innerWidth < 768; // md breakpoint
      setIsNarrowScreen(isNarrow);
      if (isNarrow) {
        setShowCardList(false);
      }
    };

    checkScreenWidth();
    window.addEventListener('resize', checkScreenWidth);

    return () => {
      window.removeEventListener('resize', checkScreenWidth);
    };
  }, []);

  // Handle sidebar resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      if (newWidth >= 200 && newWidth <= 600) { // Min 200px, Max 600px
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle shortcuts when Alt key is pressed
      if (!e.altKey) return;
      
      // Prevent default browser behavior for our shortcuts
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goToPreviousCard();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNextCard().catch(error => console.error('Error navigating to next card:', error));
          break;
        case 'a':
        case 'A':
          e.preventDefault();
          // Toggle show answer
          if (!showAnswer) {
            // When showing answer, update the card state
            const currentCard = studyCards[currentIndex];
            if (currentCard && !currentCard.showAnswer) {
              const updatedCards = [...studyCards];
              updatedCards[currentIndex] = { ...currentCard, showAnswer: true };
              setStudyCards(updatedCards);
            }
          }
          setShowAnswer(!showAnswer);
          break;
        case 'i':
        case 'I':
          e.preventDefault();
          if (showAnswer) {
            handleMarkCorrect(false);
          }
          break;
        case 'c':
        case 'C':
          e.preventDefault();
          if (showAnswer) {
            handleMarkCorrect(true);
          }
          break;
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAnswer, studyCards, currentIndex, userAnswer]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const initializeSession = async () => {
    if (!deckId) return;

    try {
      // Load deck and cards data directly from API responses
      const loadedDeck = await loadDeck(deckId);
      await loadCards(deckId);
      const db = await getDatabaseService();
      const loadedCards = await db.getCards(deckId);
      
      // Load card preferences and persisted completed cards
      await loadCardPreferences(deckId);
      await loadCompletedCards(deckId);

      // Get play mode: URL params override saved preference, otherwise use saved or default to 'adaptive'
      const params = new URLSearchParams(location.search);
      const urlMode = params.get('mode') as PlayMode | null;
      const savedMode = localStorage.getItem('flashe-study-mode') as PlayMode | null;
      const mode = urlMode || savedMode || 'adaptive';
      setPlayMode(mode);

      // Save the mode if it was explicitly set via URL (user intention)
      if (urlMode) {
        localStorage.setItem('flashe-study-mode', urlMode);
      }
      
      // Initialize study session with loaded data
      await createStudySession(mode, loadedDeck || undefined, loadedCards);
      setIsInitializing(false);
    } catch (error) {
      console.error('Failed to initialize session:', error);
      setIsInitializing(false);
    }
  };

  const createStudySession = async (mode: PlayMode, loadedDeck?: Deck, loadedCards?: Card[]) => {
    // Use passed data if available, otherwise fall back to state
    const currentDeck = loadedDeck || state.currentDeck;
    const currentCards = loadedCards || state.cards;
    
    if (!currentDeck || currentCards.length === 0) {
      return;
    }

    try {
      // Create session record
      const newSession: Session = {
        id: crypto.randomUUID(),
        deckIds: [currentDeck.id],
        startTime: new Date().toISOString(),
        cardsAttempted: 0,
        cardsCorrect: 0,
        mode
      };

      const db = await getDatabaseService();
      const sessionData = await db.createSession(newSession);
      setSession(sessionData);
      setStartTime(new Date());

      // Prepare cards for study - always keep original order for display
      let cardsToStudy = [...currentCards];
      
      // Only modify order for non-random special modes
      switch (mode) {
        case 'failed':
          // Get cards that were answered incorrectly in recent sessions
          cardsToStudy = await getFailedCards(cardsToStudy);
          break;
        case 'never_correct':
          // Get cards that have never been answered correctly
          cardsToStudy = await getNeverCorrectCards(cardsToStudy);
          break;
        case 'adaptive':
          // Use spaced repetition algorithm
          cardsToStudy = await getAdaptiveCards(cardsToStudy);
          break;
        default:
          // Sequential, Reverse Sequential, and Random - use cards in original order
          // Random mode will handle randomization in navigation logic
          break;
      }

      const studyCardsData: StudyCard[] = cardsToStudy.map(card => ({
        ...card,
        attempts: 0,
        correct: 0,
        showAnswer: false,
        userAnswers: {}
      }));

      // Set study cards and session state first so adaptive algorithm can access them
      setStudyCards(studyCardsData);
      setSession(sessionData);
      setIsSessionActive(true);
      
      // Set initial card index based on mode, restoring saved position if available
      let initialIndex = 0;
      const savedCardId = deckId ? localStorage.getItem(`flashe-deck-lastcard-${deckId}`) : null;
      const savedCardIndex = savedCardId ? studyCardsData.findIndex(c => c.id === savedCardId) : -1;

      if (savedCardIndex >= 0) {
        // Restore the last viewed card position
        initialIndex = savedCardIndex;
      } else if (mode === 'adaptive' && studyCardsData.length > 0) {
        // For adaptive mode, use the probabilistic algorithm to select the first card
        // Create a temporary version of getAdaptiveCardIndex that works with the new data
        try {
          initialIndex = await getAdaptiveCardIndexForInitialization(studyCardsData, sessionData);
        } catch (error) {
          console.error('Failed to get adaptive initial card, using first card:', error);
          initialIndex = 0;
        }
      } else if (mode === 'reverse_sequential' && studyCardsData.length > 0) {
        // For reverse sequential mode, start from the last card
        initialIndex = studyCardsData.length - 1;
      }
      
      setCurrentIndex(initialIndex);
      setResponseStartTime(new Date());

      // Don't clear userAnswer when switching decks - preserve user's work
      // setUserAnswer(''); // Removed to preserve current work
      
      // Initialize random/adaptive mode history
      if (mode === 'random' || mode === 'adaptive') {
        setRandomHistory([initialIndex]);
        setRandomHistoryIndex(0);
      } else {
        setRandomHistory([]);
        setRandomHistoryIndex(-1);
      }

      // Set initial language if available
      if (studyCardsData.length > 0) {
        const availableLanguages = Object.keys(studyCardsData[0].languages);
        if (availableLanguages.length > 0) {
          setSelectedLanguage(availableLanguages[0]);
          setUserSelectedLanguage(availableLanguages[0]);
        }
      }
    } catch (error) {
      console.error('Failed to create study session:', error);
    }
  };;;;

  const getRandomCardIndex = (excludeRecent: boolean = true): number => {
    const activeIndices = getActiveCardIndices();
    
    if (activeIndices.length === 0) return 0;
    if (activeIndices.length === 1) return activeIndices[0];
    
    let availableIndices = [...activeIndices];
    
    // Exclude recent cards to avoid immediate repeats (last 20% of active cards or minimum 2)
    if (excludeRecent && randomHistory.length > 0) {
      const recentCount = Math.max(2, Math.floor(activeIndices.length * 0.2));
      const recentCards = randomHistory.slice(-recentCount);
      availableIndices = availableIndices.filter(index => !recentCards.includes(index));
      
      // If we've excluded everything, just exclude the current card
      if (availableIndices.length === 0) {
        availableIndices = activeIndices.filter(i => i !== currentIndex);
        
        // If still empty (only 1 active card), include all active cards
        if (availableIndices.length === 0) {
          availableIndices = activeIndices;
        }
      }
    }
    
    return availableIndices[Math.floor(Math.random() * availableIndices.length)];
  };

  const getFailedCards = async (cards: Card[]): Promise<Card[]> => {
    // TODO: Implement logic to get recently failed cards
    // For now, return all cards
    return cards;
  };

  const getNeverCorrectCards = async (cards: Card[]): Promise<Card[]> => {
    // TODO: Implement logic to get never correct cards
    // For now, return all cards
    return cards;
  };

  const getAdaptiveCards = async (cards: Card[]): Promise<Card[]> => {
    // For adaptive mode, return cards in original order - selection logic is in getAdaptiveCardIndex
    return cards;
  };

  // Special version of adaptive card selection for initialization that doesn't rely on component state
  const getAdaptiveCardIndexForInitialization = async (cards: StudyCard[], sessionData: Session): Promise<number> => {
    const activeIndices = cards
      .map((_, index) => index)
      .filter(index => !isCardExcluded(cards[index].id));

    if (activeIndices.length === 0) return 0;
    if (activeIndices.length === 1) return activeIndices[0];

    try {
      const db = await getDatabaseService();
      // Get performance data for all active cards
      const cardWeights: { index: number; weight: number }[] = [];

      for (const index of activeIndices) {
        const card = cards[index];

        // Historical data (all time)
        const historicalStats = await db.getCardAttemptStats(card.id);
        const historicalSuccessRate = historicalStats.totalAttempts > 0
          ? historicalStats.correctAttempts / historicalStats.totalAttempts
          : 0;

        // Session data (current session) - will be empty for initialization
        const sessionStats = await db.getSessionCardStats(card.id, sessionData.id);
        const sessionSuccessRate = sessionStats.sessionAttempts > 0
          ? sessionStats.sessionCorrect / sessionStats.sessionAttempts
          : 0;

        // Calculate weight using the algorithm
        const weight = calculateCardWeight(
          historicalStats.totalAttempts,
          historicalSuccessRate,
          sessionStats.sessionAttempts,
          sessionSuccessRate
        );

        cardWeights.push({ index, weight });
      }

      // No need to exclude recent cards for initialization
      // Use weighted random selection
      return selectWeightedRandomCard(cardWeights);

    } catch (error) {
      console.error('Error in adaptive card selection initialization, falling back to random:', error);
      // Fallback to simple random selection
      return activeIndices[Math.floor(Math.random() * activeIndices.length)];
    }
  };

  // Adaptive mode card selection with weighted probability based on progress data
  const getAdaptiveCardIndex = async (excludeRecent: boolean = true): Promise<number> => {
    const activeIndices = getActiveCardIndices();

    if (activeIndices.length === 0) return 0;
    if (activeIndices.length === 1) return activeIndices[0];

    try {
      const db = await getDatabaseService();
      // Get performance data for all active cards
      const cardWeights: { index: number; weight: number }[] = [];

      for (const index of activeIndices) {
        const card = studyCards[index];

        // Historical data (all time)
        const historicalStats = await db.getCardAttemptStats(card.id);
        const historicalSuccessRate = historicalStats.totalAttempts > 0
          ? historicalStats.correctAttempts / historicalStats.totalAttempts
          : 0;

        // Session data (current session)
        const sessionStats = session
          ? await db.getSessionCardStats(card.id, session.id)
          : { sessionAttempts: 0, sessionCorrect: 0, sessionPercentageCorrect: 0 };
        const sessionSuccessRate = sessionStats.sessionAttempts > 0
          ? sessionStats.sessionCorrect / sessionStats.sessionAttempts
          : 0;

        // Calculate weight using the algorithm
        const weight = calculateCardWeight(
          historicalStats.totalAttempts,
          historicalSuccessRate,
          sessionStats.sessionAttempts,
          sessionSuccessRate
        );

        cardWeights.push({ index, weight });
      }

      // Exclude recently shown cards to avoid immediate repeats
      let availableWeights = [...cardWeights];
      if (excludeRecent && randomHistory.length > 0) {
        const recentCount = Math.max(2, Math.floor(activeIndices.length * 0.2));
        const recentCards = randomHistory.slice(-recentCount);
        availableWeights = cardWeights.filter(({ index }) => !recentCards.includes(index));

        // If we've excluded everything, just exclude the current card
        if (availableWeights.length === 0) {
          availableWeights = cardWeights.filter(({ index }) => index !== currentIndex);

          // If still empty, include all cards
          if (availableWeights.length === 0) {
            availableWeights = cardWeights;
          }
        }
      }

      // Use weighted random selection
      return selectWeightedRandomCard(availableWeights);

    } catch (error) {
      console.error('Error in adaptive card selection, falling back to random:', error);
      // Fallback to simple random selection
      return activeIndices[Math.floor(Math.random() * activeIndices.length)];
    }
  };
  
  // Calculate weight for a card based on progress data
  const calculateCardWeight = (
    historicalAttempts: number,
    historicalSuccessRate: number,
    sessionAttempts: number,
    sessionSuccessRate: number
  ): number => {
    const baseWeight = 1.0;
    
    // For cards with no history, give them high priority
    if (historicalAttempts === 0 && sessionAttempts === 0) {
      return baseWeight * 3.0; // High weight for new cards
    }
    
    // Success rate factors (lower success rate = higher weight)
    const historicalSuccessFactor = historicalAttempts > 0 
      ? Math.max(0.1, 1 - historicalSuccessRate) 
      : 1.0;
    const sessionSuccessFactor = sessionAttempts > 0 
      ? Math.max(0.1, 1 - sessionSuccessRate) 
      : 1.0;
    
    // Attempt count factors (fewer attempts = higher weight)
    const historicalAttemptFactor = 1 / (1 + historicalAttempts * 0.1);
    const sessionAttemptFactor = 1 / (1 + sessionAttempts * 0.2);
    
    // Weighted combination (historical data weighted more heavily)
    const combinedSuccessFactor = (0.7 * historicalSuccessFactor) + (0.3 * sessionSuccessFactor);
    const combinedAttemptFactor = (0.6 * historicalAttemptFactor) + (0.4 * sessionAttemptFactor);
    
    return baseWeight * combinedSuccessFactor * combinedAttemptFactor;
  };
  
  // Select a card using weighted random selection
  const selectWeightedRandomCard = (cardWeights: { index: number; weight: number }[]): number => {
    const totalWeight = cardWeights.reduce((sum, { weight }) => sum + weight, 0);
    
    if (totalWeight === 0) {
      // All weights are zero, fall back to uniform random
      return cardWeights[Math.floor(Math.random() * cardWeights.length)].index;
    }
    
    const random = Math.random() * totalWeight;
    let currentWeight = 0;
    
    for (const { index, weight } of cardWeights) {
      currentWeight += weight;
      if (random <= currentWeight) {
        return index;
      }
    }
    
    // Fallback (shouldn't reach here)
    return cardWeights[cardWeights.length - 1].index;
  };

  const handleShowAnswer = () => {
    setShowAnswer(true);
    const currentCard = studyCards[currentIndex];
    if (currentCard && !currentCard.showAnswer) {
      const updatedCards = [...studyCards];
      updatedCards[currentIndex] = { ...currentCard, showAnswer: true };
      setStudyCards(updatedCards);
    }
  };

  const handleAIEvaluate = async () => {
    const currentCard = studyCards[currentIndex];
    const userAnswerForLanguage = currentCard.userAnswers[userSelectedLanguage] || '';
    
    if (!userAnswerForLanguage.trim() || isEvaluating || aiSettings.provider === 'disabled') return;

    const referenceAnswer = currentCard.languages[selectedLanguage];
    
    if (!referenceAnswer) {
      setEvaluationError('No reference solution available for the selected language');
      return;
    }

    setIsEvaluating(true);
    setEvaluationError(null);
    setAIEvaluation(null);

    try {
      const evaluation = await evaluateAnswerViaFactory(
        aiSettings,
        userAnswerForLanguage,
        referenceAnswer,
        currentCard.question,
        selectedLanguage
      );
      
      setAIEvaluation(evaluation);
      setShowAnswer(true);
      
      // Update card state
      const updatedCards = [...studyCards];
      updatedCards[currentIndex] = { 
        ...currentCard, 
        showAnswer: true
      };
      setStudyCards(updatedCards);
    } catch (error) {
      console.error('AI evaluation failed:', error);
      setEvaluationError(error instanceof Error ? error.message : 'AI evaluation failed');
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleMarkCorrect = async (isCorrect: boolean) => {
    if (!session || currentIndex >= studyCards.length) return;

    const currentCard = studyCards[currentIndex];
    const responseTime = responseStartTime ? Date.now() - responseStartTime.getTime() : 0;

    try {
      const db = await getDatabaseService();
      // Record performance with AI evaluation data
      await db.createPerformanceRecord({
        cardId: currentCard.id,
        sessionId: session.id,
        isCorrect,
        responseTime,
        timestamp: new Date().toISOString(),
        easeFactor: 2.5, // TODO: Calculate based on spaced repetition
        interval: 1,
        repetitions: 0,
        aiEvaluation: aiEvaluation || undefined,
        manualOverride: aiEvaluation ? aiEvaluation.isCorrect !== isCorrect : false,
        userAnswer: userAnswer || undefined
      });

      // Update card state
      const updatedCards = [...studyCards];
      updatedCards[currentIndex] = {
        ...currentCard,
        attempts: currentCard.attempts + 1,
        correct: currentCard.correct + (isCorrect ? 1 : 0)
      };
      setStudyCards(updatedCards);

      // Update completed cards
      if (isCorrect) {
        setCompletedCards(prev => new Set(prev).add(currentCard.id));
      } else if (completedCards.has(currentCard.id)) {
        // Card was previously correct but now marked incorrect - reset its progress
        await db.resetCardProgress(currentCard.id);
        setCompletedCards(prev => {
          const next = new Set(prev);
          next.delete(currentCard.id);
          return next;
        });
      }

      // Update session stats
      const updatedSession = {
        ...session,
        cardsAttempted: session.cardsAttempted + 1,
        cardsCorrect: session.cardsCorrect + (isCorrect ? 1 : 0)
      };
      // Update session in database
      await db.updateSession(session.id, updatedSession);

      // Force state update with new object reference
      setSession({ ...updatedSession });

      // Move to next card
      goToNextCard().catch(error => console.error('Error navigating to next card:', error));

    } catch (error) {
      console.error('Failed to record performance:', error);
    }
  };

  const goToNextCard = async () => {
    let nextIndex: number | null = null;
    
    if (playMode === 'random' || playMode === 'adaptive') {
      // Random/Adaptive mode: check if we can go forward in history, otherwise pick new card
      if (randomHistoryIndex < randomHistory.length - 1) {
        // Move forward in existing history
        setRandomHistoryIndex(randomHistoryIndex + 1);
        nextIndex = randomHistory[randomHistoryIndex + 1];
      } else {
        // Pick a new card and add to history
        if (playMode === 'adaptive') {
          nextIndex = await getAdaptiveCardIndex();
        } else {
          nextIndex = getRandomCardIndex();
        }
        const newHistory = [...randomHistory, nextIndex];
        setRandomHistory(newHistory);
        setRandomHistoryIndex(newHistory.length - 1);
      }
    } else if (playMode === 'reverse_sequential') {
      // Reverse sequential mode - get previous active card (going backwards)
      nextIndex = getPreviousActiveCardIndex(currentIndex);
      if (nextIndex === null) {
        // Beginning of deck in reverse sequential mode - show completion dialog
        showSequentialCompletionDialog();
        return;
      }
    } else {
      // Sequential mode - get next active card
      nextIndex = getNextActiveCardIndex(currentIndex);
      if (nextIndex === null) {
        // End of deck in sequential mode - show completion dialog
        showSequentialCompletionDialog();
        return;
      }
    }
    
    if (nextIndex !== null) {
      setCurrentIndex(nextIndex);
      setShowAnswer(false);
      const newCard = studyCards[nextIndex];
      
      // Clear user answers for this card
      const updatedCards = [...studyCards];
      updatedCards[nextIndex] = { 
        ...newCard, 
        userAnswers: {} 
      };
      setStudyCards(updatedCards);
      
      setUserAnswer('');
      setResponseStartTime(new Date());
      
      // Clear AI evaluation state
      setAIEvaluation(null);
      setEvaluationError(null);
    }
  };;

  const goToPreviousCard = () => {
    let prevIndex: number;
    
    if (playMode === 'random' || playMode === 'adaptive') {
      // Random/Adaptive mode: go back in history if available
      if (randomHistoryIndex > 0) {
        setRandomHistoryIndex(randomHistoryIndex - 1);
        prevIndex = randomHistory[randomHistoryIndex - 1];
      } else {
        // Can't go back further in random/adaptive mode
        return;
      }
    } else if (playMode === 'reverse_sequential') {
      // Reverse sequential mode - get next active card (going forwards)
      const nextActiveIndex = getNextActiveCardIndex(currentIndex);
      if (nextActiveIndex !== null) {
        prevIndex = nextActiveIndex;
      } else {
        // Can't go back further (we're at the end of the deck)
        return;
      }
    } else {
      // Sequential mode - get previous active card
      const prevActiveIndex = getPreviousActiveCardIndex(currentIndex);
      if (prevActiveIndex !== null) {
        prevIndex = prevActiveIndex;
      } else {
        // Can't go back further
        return;
      }
    }
    
    setCurrentIndex(prevIndex);
    setShowAnswer(false);
    const newCard = studyCards[prevIndex];
    
    // Clear user answers for this card
    const updatedCards = [...studyCards];
    updatedCards[prevIndex] = { 
      ...newCard, 
      userAnswers: {} 
    };
    setStudyCards(updatedCards);
    
    setUserAnswer('');
    setResponseStartTime(new Date());
    
    // Clear AI evaluation state
    setAIEvaluation(null);
    setEvaluationError(null);
  };;

  const goToCard = (index: number) => {
    if (index >= 0 && index < studyCards.length) {
      setCurrentIndex(index);
      setShowAnswer(false);
      const newCard = studyCards[index];
      
      // Clear user answers for this card
      const updatedCards = [...studyCards];
      updatedCards[index] = { 
        ...newCard, 
        userAnswers: {} 
      };
      setStudyCards(updatedCards);
      
      setUserAnswer('');
      setResponseStartTime(new Date());
      
      // Clear AI evaluation state
      setAIEvaluation(null);
      setEvaluationError(null);
      
      // Hide card list on narrow screens after selection
      if (isNarrowScreen) {
        setShowCardList(false);
      }
    }
  };;

  const endSession = async () => {
    if (!session || !startTime) return;

    try {
      const endTime = new Date();
      const duration = Math.round((endTime.getTime() - startTime.getTime()) / 1000);

      const db = await getDatabaseService();
      await db.updateSession(session.id, {
        endTime: endTime.toISOString(),
        duration
      });

      // Navigate directly to home without showing summary
      navigate('/');
    } catch (error) {
      console.error('Failed to end session:', error);
      // Navigate anyway even if session update fails
      navigate('/');
    }
  };

  const showSequentialCompletionDialog = () => {
    if (!session) return;
    
    const accuracy = session.cardsAttempted > 0 
      ? Math.round((session.cardsCorrect / session.cardsAttempted) * 100)
      : 0;

    const message = `Sequential deck complete!\n\nCards Attempted: ${session.cardsAttempted}\nCards Correct: ${session.cardsCorrect}\nAccuracy: ${accuracy}%\n\nWould you like to continue from the beginning or start over with fresh metrics?`;
    
    const shouldContinue = window.confirm(message + '\n\nClick OK to Continue (keep metrics) or Cancel to Start Over (clear metrics)');
    
    if (shouldContinue) {
      // Continue: keep metrics, restart from beginning with first active card
      const firstActiveIndex = activeCardIndices.length > 0 ? activeCardIndices[0] : 0;
      setCurrentIndex(firstActiveIndex);
      setShowAnswer(false);
      const firstCard = studyCards[firstActiveIndex];
      setUserAnswer(firstCard.userAnswers[userSelectedLanguage] || '');
      setResponseStartTime(new Date());
    } else {
      // Start Over: clear metrics and restart session
      restartSession();
    }
  };

  const switchMode = (newMode: PlayMode) => {
    setPlayMode(newMode);

    // Persist the mode preference for future sessions
    localStorage.setItem('flashe-study-mode', newMode);

    if (newMode === 'random' || newMode === 'adaptive') {
      // Switching to random/adaptive mode: initialize history with current card
      setRandomHistory([currentIndex]);
      setRandomHistoryIndex(0);
    } else if (newMode === 'sequential' || newMode === 'reverse_sequential') {
      // Switching to sequential or reverse sequential mode: find position of current card in original order
      // Current card might be from random history, so we keep the current position
      // Random history is cleared since we're now in sequential mode
      setRandomHistory([]);
      setRandomHistoryIndex(-1);
    }
    
    // Update session mode in database
    if (session) {
      getDatabaseService().then(db => {
        return db.updateSession(session.id, { mode: newMode });
      }).catch(error => {
        console.error('Failed to update session mode:', error);
      });
    }
  };;

  const restartSession = async () => {
    if (!state.currentDeck) return;

    try {
      // End current session
      if (session && startTime) {
        const endTime = new Date();
        const duration = Math.round((endTime.getTime() - startTime.getTime()) / 1000);
        const db = await getDatabaseService();
        await db.updateSession(session.id, {
          endTime: endTime.toISOString(),
          duration
        });
      }

      // Create fresh session
      await createStudySession(playMode);
    } catch (error) {
      console.error('Failed to restart session:', error);
    }
  };

  const showSessionSummary = () => {
    if (!session) return;
    
    const accuracy = session.cardsAttempted > 0 
      ? Math.round((session.cardsCorrect / session.cardsAttempted) * 100)
      : 0;

    alert(`Session Complete!\n\nCards Attempted: ${session.cardsAttempted}\nCards Correct: ${session.cardsCorrect}\nAccuracy: ${accuracy}%`);
    navigate('/');
  };

  const exitSession = () => {
    // Remember the current card position for this deck
    if (deckId && studyCards.length > 0 && studyCards[currentIndex]) {
      localStorage.setItem(`flashe-deck-lastcard-${deckId}`, studyCards[currentIndex].id);
    }

    if (isSessionActive && session) {
      endSession();
    } else {
      navigate('/');
    }
  };

  const getCardStatusIcon = (card: StudyCard, index: number) => {
    if (index === currentIndex) {
      return (
        <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
      );
    } else if (completedCards.has(card.id)) {
      return (
        <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      );
    } else if (card.attempts > 0) {
      return (
        <svg className="w-3 h-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-8a1 1 0 100-2 1 1 0 000 2zm1-3a1 1 0 011 1v4a1 1 0 11-2 0V8a1 1 0 011-1z" clipRule="evenodd" />
        </svg>
      );
    } else {
      return (
        <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
      );
    }
  };

  const handleEditCard = () => {
    if (state.currentDeck && studyCards[currentIndex]) {
      navigate(`/deck/${state.currentDeck.id}/card/${studyCards[currentIndex].id}/edit`);
    }
  };

  const handleOpenCodePopup = (code: string, language: string, title: string) => {
    setPopupCode(code);
    setPopupLanguage(language);
    setPopupTitle(title);
    setShowCodePopup(true);
  };

  const handleCloseCodePopup = () => {
    setShowCodePopup(false);
  };

  if (state.loading || isInitializing || !isSessionActive || studyCards.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            {isInitializing 
              ? 'Initializing study session...' 
              : studyCards.length === 0 
                ? 'No cards available for study' 
                : 'Loading study session...'}
          </p>
          {studyCards.length === 0 && !isInitializing && (
            <button
              onClick={() => navigate('/')}
              className="mt-4 btn btn-primary"
            >
              Back to Decks
            </button>
          )}
        </div>
      </div>
    );
  }

  const currentCard = studyCards[currentIndex];
  
  // Handle race condition where currentCard might be undefined during initialization
  if (!currentCard) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading card data...</p>
        </div>
      </div>
    );
  }
  
  const activeCardIndices = getActiveCardIndices();
  const currentActivePosition = activeCardIndices.indexOf(currentIndex) + 1;
  const totalActiveCards = activeCardIndices.length;
  // For reverse sequential mode, progress is calculated from end to beginning
  const progress = totalActiveCards > 0
    ? playMode === 'reverse_sequential'
      ? Math.round(((totalActiveCards - currentActivePosition + 1) / totalActiveCards) * 100)
      : Math.round((currentActivePosition / totalActiveCards) * 100)
    : 0;
  const availableLanguages = Object.keys(currentCard.languages);

  return (
    <div className="fixed inset-0 bg-gray-50 dark:bg-gray-900 overflow-scroll">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Toggle Card List Button */}
            <button
              onClick={() => setShowCardList(!showCardList)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title={showCardList ? 'Hide card list' : 'Show card list'}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
            <div>
              <div className="flex items-center space-x-4">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {state.currentDeck?.name}
                </h1>
                {session && (
                  <div className="hidden md:flex items-center space-x-3 text-xs text-gray-600 dark:text-gray-400">
                    <span>Attempted: {session.cardsAttempted}</span>
                    <span>Correct: {session.cardsCorrect}</span>
                    <span>
                      Accuracy: {session.cardsAttempted > 0
                        ? Math.round((session.cardsCorrect / session.cardsAttempted) * 100)
                        : 0}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Session metrics, mode toggle, progress, close button */}
          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center space-x-4">
            {/* Mode Toggle Button */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  let newMode: PlayMode;
                  if (playMode === 'sequential') {
                    newMode = 'reverse_sequential';
                  } else if (playMode === 'reverse_sequential') {
                    newMode = 'random';
                  } else if (playMode === 'random') {
                    newMode = 'adaptive';
                  } else {
                    newMode = 'sequential';
                  }
                  switchMode(newMode);
                }}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  playMode === 'sequential'
                    ? 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-800/30'
                    : playMode === 'reverse_sequential'
                    ? 'bg-orange-100 text-orange-800 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:hover:bg-orange-800/30'
                    : playMode === 'random'
                    ? 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-800/30'
                    : 'bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:hover:bg-purple-800/30'
                }`}
                title={`Current: ${playMode === 'reverse_sequential' ? 'Reverse Sequential' : playMode.charAt(0).toUpperCase() + playMode.slice(1)} mode. Click to cycle modes.`}
              >
                {playMode === 'sequential' ? 'Sequential' : playMode === 'reverse_sequential' ? 'Reverse' : playMode === 'random' ? 'Random' : 'Adaptive'}
              </button>
            </div>
            {/* Skip Completed Cards Toggle */}
            <button
              onClick={handleSkipCompletedCardsToggle}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                playerSettings.skipCompletedCards
                  ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-800/30'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
              }`}
              title={playerSettings.skipCompletedCards ? 'Skip completed cards: ON' : 'Skip completed cards: OFF'}
            >
              {playerSettings.skipCompletedCards ? 'Skip Correct' : 'Show All'}
            </button>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {playMode === 'random' || playMode === 'adaptive'
                ? `Card ${currentIndex + 1} (${playMode === 'adaptive' ? 'Adaptive' : 'Random'})${totalActiveCards < studyCards.length ? ` • ${totalActiveCards} active` : ''}`
                : playMode === 'reverse_sequential'
                ? `${totalActiveCards - currentActivePosition + 1} of ${totalActiveCards} (Reverse)${totalActiveCards < studyCards.length ? ` • ${studyCards.length} total` : ''}`
                : `${currentActivePosition} of ${totalActiveCards} active${totalActiveCards < studyCards.length ? ` (${studyCards.length} total)` : ''}`
              }
            </div>
            {playMode !== 'random' && playMode !== 'adaptive' && (
              <div className="w-32 bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            )}
            </div>
            <button
              onClick={exitSession}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title="Close deck"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="flex relative">
        {/* Card List Sidebar */}
        {showCardList && (
          <div
            ref={navigationContainerRef}
            className={`bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto ${
              isNarrowScreen ? 'fixed inset-y-0 left-0 z-50 shadow-2xl' : 'relative'
            }`}
            style={{
              width: isNarrowScreen ? '85vw' : `${sidebarWidth}px`,
              height: 'calc(100vh - 3em)',
              top: isNarrowScreen ? '3em' : 'auto'
            }}
          >
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="font-semibold text-gray-900 dark:text-white text-sm">Cards</h2>
                  {totalActiveCards < studyCards.length && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {totalActiveCards} active of {studyCards.length}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setShowCardList(false)}
                  className="btn btn-secondary text-xs px-2 py-1"
                  title="Hide cards menu"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-1">
                {studyCards.map((card, index) => (
                  <div
                    key={card.id}
                    ref={(el) => (cardItemRefs.current[index] = el)}
                    className={`flex items-center p-2 rounded-lg transition-colors ${
                      index === currentIndex
                        ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    } ${isCardExcluded(card.id) ? 'opacity-60' : ''}`}
                  >
                    <button
                      onClick={() => goToCard(index)}
                      className="flex-1 text-left flex items-center space-x-3"
                    >
                      {getCardStatusIcon(card, index)}
                      <div className="flex-1">
                        <div className={`text-sm font-medium truncate ${
                          isCardExcluded(card.id) 
                            ? 'line-through text-gray-500 dark:text-gray-400' 
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {card.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {card.category} • {card.difficulty}
                        </div>
                      </div>
                    </button>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={!isCardExcluded(card.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleCardPreferenceToggle(card.id, !e.target.checked);
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        title={isCardExcluded(card.id) ? "Include card in navigation" : "Exclude card from navigation"}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Resize Handle - only on wide screens */}
            {!isNarrowScreen && (
              <div
                className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 hover:opacity-50 transition-colors"
                onMouseDown={handleResizeStart}
                title="Drag to resize"
              />
            )}
          </div>
        )}

        {/* Backdrop overlay for narrow screens */}
        {showCardList && isNarrowScreen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            style={{ top: '3em' }}
            onClick={() => setShowCardList(false)}
          />
        )}

        {/* Main Study Area */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 p-4">
            <div className="mx-2 md:mx-8 flex flex-col">
              {/* Question Card */}
              <div className="card flex flex-col min-h-screen w-full">
              <div className="card-body flex flex-col">
                {/* Fixed Header Content */}
                <div className="flex-shrink-0">
                  {/* Question with inline chips */}
                  <div className="flex flex-col-reverse md:flex-row md:justify-between md:items-start mb-4">
                    <p className="text-gray-700 dark:text-gray-300 font-medium md:flex-1 md:pr-4">
                      {currentCard.question}
                    </p>
                    <div className="flex items-center space-x-2 flex-shrink-0 mb-2 md:mb-0">
                      <button
                        onClick={handleEditCard}
                        className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                        title="Edit card"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <input
                        type="checkbox"
                        checked={!isCardExcluded(currentCard.id)}
                        onChange={(e) => {
                          handleCardPreferenceToggle(currentCard.id, !e.target.checked);
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        title={isCardExcluded(currentCard.id) ? "Include card in navigation" : "Exclude card from navigation"}
                      />
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 rounded text-xs">
                        {currentCard.category}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        currentCard.difficulty === 'Easy'
                          ? 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900'
                          : currentCard.difficulty === 'Medium'
                          ? 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900'
                          : 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
                      }`}>
                        {currentCard.difficulty}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-between items-center mb-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        if (!showAnswer) {
                          // When showing answer, update the card state like handleShowAnswer did
                          const currentCard = studyCards[currentIndex];
                          if (currentCard && !currentCard.showAnswer) {
                            const updatedCards = [...studyCards];
                            updatedCards[currentIndex] = { ...currentCard, showAnswer: true };
                            setStudyCards(updatedCards);
                          }
                        }
                        setShowAnswer(!showAnswer);
                      }}
                      className="btn btn-secondary text-sm px-3 py-1"
                      title={`${showAnswer ? 'Hide Answer' : 'Show Answer'} (Alt+A)`}
                    >
                      {showAnswer ? 'Hide Answer' : 'Show Answer'}
                    </button>
                    
                    {/* AI Evaluate Button */}
                    {aiSettings.provider !== 'disabled' && !showAnswer && (
                      <button
                        onClick={handleAIEvaluate}
                        disabled={!(currentCard.userAnswers[userSelectedLanguage] || '').trim() || isEvaluating}
                        className="btn btn-primary text-sm px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Get AI evaluation of your answer"
                      >
                        {isEvaluating ? (
                          <div className="flex items-center space-x-1">
                            <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Evaluating...</span>
                          </div>
                        ) : (
                          'AI Evaluate'
                        )}
                      </button>
                    )}
                    {showAnswer && (
                      <>
                        <button
                          onClick={() => handleMarkCorrect(false)}
                          className="btn btn-danger text-sm px-3 py-1"
                          title="Mark as Incorrect (Alt+I)"
                        >
                          Incorrect
                        </button>
                        <button
                          onClick={() => handleMarkCorrect(true)}
                          className="btn btn-success text-sm px-3 py-1"
                          title="Mark as Correct (Alt+C)"
                        >
                          Correct
                        </button>
                      </>
                    )}
                  </div>
                  
                  <button
                    onClick={() => {
                      setUserAnswer('');
                      setShowAnswer(false);
                      // Update card state to clear user answer
                      const currentCard = studyCards[currentIndex];
                      if (currentCard) {
                        const updatedCards = [...studyCards];
                        updatedCards[currentIndex] = { ...currentCard, showAnswer: false, userAnswers: {} };
                        setStudyCards(updatedCards);
                      }
                    }}
                    className="btn btn-secondary text-sm px-3 py-1 flex items-center"
                    title="Reset card - clear answer and hide solution"
                  >
                    Reset
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                  </div>
                </div>

                {/* Expandable Solution Areas */}
                <div className="flex-1 flex flex-col gap-4">
                  {/* User Code Input */}
                  <div className="flex flex-col">
                    <div className="flex items-center justify-between mb-2 flex-shrink-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">
                          Your Solution:
                        </h3>
                        {(currentCard.userAnswers[userSelectedLanguage] || '').trim() && (
                          <button
                            onClick={() => {
                              handleOpenCodePopup(
                                currentCard.userAnswers[userSelectedLanguage] || '',
                                userSelectedLanguage,
                                'Your Solution'
                              );
                            }}
                            className="text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
                            title="View in popup"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                          </button>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {availableLanguages.map(lang => (
                          <button
                            key={lang}
                            onClick={() => {
                              setUserSelectedLanguage(lang);
                              setSelectedLanguage(lang); // Sync reference solution to match
                              setUserAnswer(currentCard.userAnswers[lang] || '');
                            }}
                            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                              userSelectedLanguage === lang
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                            }`}
                          >
                            {lang.charAt(0).toUpperCase() + lang.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="codemirror-container codemirror-enhanced border border-gray-300 dark:border-gray-600 rounded overflow-auto w-full min-w-0" style={{ height: '20vh' }}>
                      <CodeMirror
                        height="20vh"
                        value={currentCard.userAnswers[userSelectedLanguage] || ''}
                        editable={true}
                        onChange={(value) => {
                          const updatedCards = [...studyCards];
                          updatedCards[currentIndex] = {
                            ...currentCard,
                            userAnswers: {
                              ...currentCard.userAnswers,
                              [userSelectedLanguage]: value
                            }
                          };
                          setStudyCards(updatedCards);
                          setUserAnswer(value);
                        }}
                        extensions={[
                          getLanguageExtension(userSelectedLanguage),
                          ...(getThemeExtension(state.theme === 'dark') ? [getThemeExtension(state.theme === 'dark')!] : []),
                          EditorView.theme({
                            '.cm-editor': { height: '100%', width: '100%', maxWidth: '100%' },
                            '.cm-scroller': {
                              height: '100%',
                              maxWidth: '100%',
                              overflowX: 'auto',
                              overflowY: 'auto'
                            },
                            '.cm-content': { padding: '10px' }
                          })
                        ]}
                        theme={state.theme === 'dark' ? 'dark' : 'light'}
                        basicSetup={{
                          lineNumbers: true,
                          foldGutter: false,
                          dropCursor: true,
                          allowMultipleSelections: false,
                          indentOnInput: true,
                          bracketMatching: true,
                          closeBrackets: true,
                          autocompletion: false,
                          highlightSelectionMatches: false
                        }}
                      />
                    </div>
                  </div>

                  {/* Reference Answer */}
                  <div className="flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-2 flex-shrink-0">
                      <h3 className="text-base font-medium text-gray-900 dark:text-white">
                        Reference Solution:
                      </h3>
                      <div className="flex items-center space-x-2">
                        {availableLanguages.map(lang => (
                          <button
                            key={lang}
                            onClick={() => {
                              setSelectedLanguage(lang); // Only update reference solution language
                            }}
                            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                              selectedLanguage === lang
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                            }`}
                          >
                            {lang.charAt(0).toUpperCase() + lang.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div
                      className="codemirror-container readonly border border-gray-300 dark:border-gray-600 rounded overflow-auto cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors w-full min-w-0"
                      style={{ height: '20vh' }}
                      onClick={() => {
                        if (showAnswer && currentCard.languages[selectedLanguage]) {
                          handleOpenCodePopup(
                            currentCard.languages[selectedLanguage],
                            selectedLanguage,
                            'Reference Solution'
                          );
                        }
                      }}
                      title={showAnswer ? "Click to view in popup" : "Show answer to view code"}
                    >
                      <CodeMirror
                        height="20vh"
                        value={showAnswer && currentCard.languages[selectedLanguage] ? currentCard.languages[selectedLanguage] : ''}
                        extensions={[
                          getLanguageExtension(selectedLanguage),
                          ...(getThemeExtension(state.theme === 'dark') ? [getThemeExtension(state.theme === 'dark')!] : []),
                          EditorView.theme({
                            '.cm-editor': { height: '100%', width: '100%', maxWidth: '100%' },
                            '.cm-scroller': {
                              height: '100%',
                              maxWidth: '100%',
                              overflowX: 'auto',
                              overflowY: 'auto'
                            },
                            '.cm-content': { padding: '10px' }
                          })
                        ]}
                        theme={state.theme === 'dark' ? 'dark' : 'light'}
                        editable={false}
                        basicSetup={{
                          lineNumbers: true,
                          foldGutter: false,
                          dropCursor: false,
                          allowMultipleSelections: false,
                          indentOnInput: false,
                          bracketMatching: false,
                          closeBrackets: false,
                          autocompletion: false,
                          highlightSelectionMatches: false,
                          searchKeymap: false
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* AI Evaluation Feedback - part of fixed header */}
                {(aiEvaluation || evaluationError) && (
                  <div className="mb-4 flex-shrink-0">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                      AI Evaluation:
                    </h3>
                    
                    {evaluationError && (
                      <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs">
                        <p className="text-red-700 dark:text-red-300">
                          <strong>Error:</strong> {evaluationError}
                        </p>
                      </div>
                    )}
                    
                    {aiEvaluation && (
                      <div className={`p-2 border rounded text-xs ${
                        aiEvaluation.isCorrect 
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                          : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                      }`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`px-1 py-0.5 rounded text-xs ${
                            aiEvaluation.isCorrect
                              ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                          }`}>
                            {aiEvaluation.isCorrect ? 'Correct' : 'Needs Improvement'}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400">
                            {Math.round(aiEvaluation.confidence * 100)}%
                          </span>
                        </div>
                        <p className="text-gray-700 dark:text-gray-300">
                          {aiEvaluation.feedback}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes - part of fixed header */}
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm flex-shrink-0">
                  <strong className="text-gray-900 dark:text-white block mb-2">Notes:</strong>
                  {showAnswer && currentCard.notes ? (
                    <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {currentCard.notes.split('\n\nMemory Aid:\n').map((section, index) => {
                        if (index === 0) {
                          // Regular notes section
                          return (
                            <div key={index} className="mb-3">
                              {section}
                            </div>
                          );
                        } else {
                          // Memory aid section
                          return (
                            <div key={index} className="memory-aid-section">
                              <div className="font-semibold text-blue-900 dark:text-blue-300 mb-2 text-base">🧠 Memory Aid:</div>
                              <div className="space-y-2">
                                {section.split('\n- ').filter(item => item.trim()).map((memoryItem, itemIndex) => {
                                  const parts = memoryItem.split(': ');
                                  if (parts.length >= 2) {
                                    const language = parts[0].replace(/^- /, '').trim();
                                    const description = parts.slice(1).join(': ').trim();
                                    return (
                                      <div key={itemIndex} className="bg-white dark:bg-gray-600 p-3 rounded-md border-l-4 border-blue-500">
                                        <div className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                                          {language}:
                                        </div>
                                        <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                          {description}
                                        </div>
                                      </div>
                                    );
                                  }
                                  return null;
                                })}
                              </div>
                            </div>
                          );
                        }
                      })}
                    </div>
                  ) : (
                    <span className="text-gray-700 dark:text-gray-300">
                      Click "Show Answer" to view notes.
                    </span>
                  )}
                </div>
                
                {/* Navigation - Integrated into card */}
                <div className="flex justify-between items-center pt-2 mt-2 flex-shrink-0">
                  <button
                    onClick={goToPreviousCard}
                    disabled={(playMode === 'random' || playMode === 'adaptive') ? randomHistoryIndex <= 0 : playMode === 'reverse_sequential' ? currentIndex === studyCards.length - 1 : currentIndex === 0}
                    className="btn btn-secondary text-sm px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    title="Previous Card (Alt+←)"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous
                  </button>

                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {playMode === 'random' || playMode === 'adaptive'
                      ? `Card ${currentIndex + 1} (${playMode === 'adaptive' ? 'Adaptive' : 'Random'})`
                      : playMode === 'reverse_sequential'
                      ? `${studyCards.length - currentIndex} of ${studyCards.length} (Reverse)`
                      : `${currentIndex + 1} of ${studyCards.length}`
                    }
                  </div>

                  <button
                    onClick={goToNextCard}
                    disabled={false} // Never disable next in random mode, only disable in sequential if at end
                    className="btn btn-secondary text-sm px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    title="Next Card (Alt+→)"
                  >
                    Next
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Show Card List Button - Only on narrow screens */}
                {isNarrowScreen && !showCardList && (
                  <div className="mt-4 flex justify-center">
                    <button
                      onClick={() => setShowCardList(true)}
                      className="btn btn-primary text-sm px-4 py-2 flex items-center space-x-2"
                      title="View all cards"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      <span>Show Card List</span>
                    </button>
                  </div>
                )}

                {/* Bottom Panel - Session metrics and controls (visible only on narrow screens) */}
                <div className="md:hidden bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 px-4 py-3 space-y-3 mt-6 -mx-6 -mb-6">
                  {/* Session Metrics */}
                  {session && (
                    <div className="flex justify-center items-center space-x-4 text-xs text-gray-600 dark:text-gray-400">
                      <span>Attempted: {session.cardsAttempted}</span>
                      <span>Correct: {session.cardsCorrect}</span>
                      <span>
                        Accuracy: {session.cardsAttempted > 0
                          ? Math.round((session.cardsCorrect / session.cardsAttempted) * 100)
                          : 0}%
                      </span>
                    </div>
                  )}

                  {/* Mode Toggle, Skip Correct, and Progress */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          let newMode: PlayMode;
                          if (playMode === 'sequential') {
                            newMode = 'reverse_sequential';
                          } else if (playMode === 'reverse_sequential') {
                            newMode = 'random';
                          } else if (playMode === 'random') {
                            newMode = 'adaptive';
                          } else {
                            newMode = 'sequential';
                          }
                          switchMode(newMode);
                        }}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          playMode === 'sequential'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : playMode === 'reverse_sequential'
                            ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                            : playMode === 'random'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                        }`}
                      >
                        {playMode === 'sequential' ? 'Sequential' : playMode === 'reverse_sequential' ? 'Reverse' : playMode === 'random' ? 'Random' : 'Adaptive'}
                      </button>
                      {/* Skip Completed Cards Toggle (mobile) */}
                      <button
                        onClick={handleSkipCompletedCardsToggle}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          playerSettings.skipCompletedCards
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}
                        title={playerSettings.skipCompletedCards ? 'Skip completed cards: ON' : 'Skip completed cards: OFF'}
                      >
                        {playerSettings.skipCompletedCards ? 'Skip Correct' : 'Show All'}
                      </button>
                    </div>

                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {playMode === 'random' || playMode === 'adaptive'
                        ? `Card ${currentIndex + 1} (${playMode === 'adaptive' ? 'Adaptive' : 'Random'})`
                        : playMode === 'reverse_sequential'
                        ? `${totalActiveCards - currentActivePosition + 1} of ${totalActiveCards} (Reverse)`
                        : `${currentActivePosition} of ${totalActiveCards} active`
                      }
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {playMode !== 'random' && playMode !== 'adaptive' && (
                    <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Code Popup Modal */}
      {showCodePopup && (
        <CodePopupModal
          code={popupCode}
          language={popupLanguage}
          title={popupTitle}
          languageExtension={getLanguageExtension(popupLanguage)}
          themeExtension={getThemeExtension(state.theme === 'dark') || undefined}
          isDarkMode={state.theme === 'dark'}
          onClose={handleCloseCodePopup}
        />
      )}
    </div>
  );
};

export default DeckPlayer;